/**
 * Plans and billing rules (contracts billing.ts, docs/features/billing.md). Pure, shared by the API (enforcement,
 * Stripe sync) and every client (the plan summary, trial banners).
 *
 * The catalog: `founding` (workspaces from before billing: no limits, never billed, never locked) and `starter`
 * (a flat price for up to 25 people and 30 GB pooled, +50 GB per storage add-on; 14-day trial without a card).
 * Pro (per seat, past 25 people) and Business slot in as more entries; AI credits will be another limit.
 */
import type { BillingAccess, BillingCurrency, BillingLimits, BillingStatus, Plan, WorkspaceBilling } from "@bundle/contracts";

/** Decimal gigabyte, like docs/architecture/storage-limits.md. */
export const GB = 1_000_000_000;
export const DAY_MS = 24 * 60 * 60 * 1000;

export interface PlanSpec {
  /** Billed through Stripe (founding never is). */
  billable: boolean;
  /** Active non-guest people; absent ⇒ unlimited. */
  members?: number;
  /** Pooled Files storage before add-ons; absent ⇒ unlimited. */
  storageBytes?: number;
  /** What one storage add-on adds. */
  addonBytes?: number;
  /** Prices in US cents: per month on monthly billing, per year on yearly billing. */
  prices?: { month: number; year: number };
  /** Price of one storage add-on per month, in cents. */
  addonMonthlyPrice?: number;
  /** The same prices in centavos: Stripe's BRL option, set by hand, not a conversion. */
  brl?: { prices: { month: number; year: number }; addonMonthlyPrice: number };
  /** Trial length for a new workspace. */
  trialDays?: number;
}

export const PLAN_CATALOG: Readonly<Record<Plan, PlanSpec>> = {
  founding: { billable: false },
  starter: {
    billable: true,
    members: 25,
    storageBytes: 30 * GB,
    addonBytes: 50 * GB,
    prices: { month: 5_900, year: 58_800 },
    addonMonthlyPrice: 500,
    brl: { prices: { month: 16_900, year: 169_900 }, addonMonthlyPrice: 1_500 },
    trialDays: 14,
  },
};

/** The plan a new workspace starts on, in its trial. */
export const NEW_WORKSPACE_PLAN: Plan = "starter";

/** The fields the rules read. */
export type BillingState = Pick<WorkspaceBilling, "plan" | "status" | "trialEndsAt" | "currentPeriodEnd" | "pastDueSince" | "storageAddons">;

/** Full access lasts this long from the first failed payment (Stripe's retries cancel the subscription by then). */
export const PAST_DUE_GRACE_DAYS = 14;
/** Data is kept this long after a workspace locks (deletion isn't built yet). */
export const LOCKED_RETENTION_DAYS = 90;

/** The plan's limits with this workspace's add-ons (absent ⇒ unlimited). Unknown plans are unlimited. */
export function limitsFor(billing: Pick<BillingState, "plan" | "storageAddons">): BillingLimits {
  const spec = PLAN_CATALOG[billing.plan] as PlanSpec | undefined;
  const out: BillingLimits = {};
  if (spec?.members !== undefined) out.members = spec.members;
  if (spec?.storageBytes !== undefined) out.storageBytes = spec.storageBytes + (spec.addonBytes ?? 0) * Math.max(0, billing.storageAddons);
  return out;
}

/** Whether `adding` more people fit with `current` already counted. */
export function canAddMembers(billing: Pick<BillingState, "plan" | "storageAddons">, current: number, adding: number): boolean {
  const max = limitsFor(billing).members;
  return max === undefined || current + adding <= max;
}

/** Whether `addingBytes` more fit in the pool with `usedBytes` stored. */
export function storageAllows(billing: Pick<BillingState, "plan" | "storageAddons">, usedBytes: number, addingBytes: number): boolean {
  const max = limitsFor(billing).storageBytes;
  return max === undefined || usedBytes + addingBytes <= max;
}

/** Whole days left in the trial, rounded up (0 once it ended, or when not trialing). */
export function trialDaysLeft(billing: Pick<BillingState, "status" | "trialEndsAt">, now: number): number {
  if (billing.status !== "trialing" || billing.trialEndsAt === undefined) return 0;
  return Math.max(0, Math.ceil((billing.trialEndsAt - now) / DAY_MS));
}

/** When a new workspace's trial ends. */
export function trialEndFor(plan: Plan, createdAt: number): number | undefined {
  const days = PLAN_CATALOG[plan].trialDays;
  return days === undefined ? undefined : createdAt + days * DAY_MS;
}


/**
 * `locked`: the trial ended with no subscription, or a canceled/expired subscription ran past its paid period (and any
 * trial left), or a payment has been failing for more than 14 days (`past_due` keeps full access until then while
 * Stripe retries; with no recorded first failure it stays full). Reads, search, export, billing and sign-out still work
 * when locked (the API blocks writes). Founding workspaces are never locked.
 */
export function billingAccess(billing: BillingState, now: number): BillingAccess {
  if (!PLAN_CATALOG[billing.plan]?.billable) return "full";
  switch (billing.status) {
    case "active":
      return "full";
    case "past_due":
      return billing.pastDueSince === undefined || now < billing.pastDueSince + PAST_DUE_GRACE_DAYS * DAY_MS ? "full" : "locked";
    case "trialing":
      return billing.trialEndsAt === undefined || now < billing.trialEndsAt ? "full" : "locked";
    case "canceled":
    case "expired": {
      const until = Math.max(billing.currentPeriodEnd ?? 0, billing.trialEndsAt ?? 0);
      return now < until ? "full" : "locked";
    }
    default:
      return "full";
  }
}

/**
 * Our status for a Stripe subscription status, or undefined when it says nothing yet (`incomplete`: the first payment
 * is still pending; `incomplete_expired`: it never happened, so the workspace keeps what it had). `unpaid` (retries
 * exhausted) and `paused` (a trial ended without a payment method) count as expired.
 */
export function statusFromStripe(status: string): BillingStatus | undefined {
  switch (status) {
    case "trialing":
      return "trialing";
    case "active":
      return "active";
    case "past_due":
      return "past_due";
    case "canceled":
      return "canceled";
    case "unpaid":
    case "paused":
      return "expired";
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------------------------------------------------
// The billing screens (Settings → Billing, the plan picker, trial banners, limit prompts): pure view state shared by
// every client (docs/features/billing.md, the approved design). Money is in US cents and dates are epoch ms; clients
// format them. Native apps never sell (`sells` false): no prices and no purchase buttons, the same copy otherwise.
// ---------------------------------------------------------------------------------------------------------------------

/** Trial banners: owners and admins from 7 days left (info), 3 (warning), and everyone on the last day (danger). */
export const TRIAL_BANNER_DAYS = { info: 7, warning: 3, danger: 1 } as const;
/** A dismissed 7- or 3-day banner stays hidden this long. */
export const BANNER_SNOOZE_MS = DAY_MS;
/** Stripe Checkout keeps a trial only with more than 48 hours left (the API's MIN_CHECKOUT_TRIAL_MS). */
export const CHECKOUT_TRIAL_MIN_MS = 49 * 60 * 60 * 1000;
/** The plan picker's add-on choices: none, or 1–4 blocks of 50 GB. */
export const STORAGE_ADDON_CHOICES = [0, 1, 2, 3, 4] as const;

/** The plan card on Settings → Billing. */
export type BillingCard =
  | { kind: "founding" }
  | { kind: "trial"; daysLeft: number; trialEndsAt: number; day: number; totalDays: number }
  | {
      kind: "active";
      interval: "month" | "year";
      /** Per month on this interval (annual shows $49 a month). */
      monthlyCents: number;
      /** What the next charge comes to, add-ons included. */
      nextCents: number;
      /** Renewal, or the last day when `ending`. */
      nextAt?: number;
      /** Canceled at the period end: it ends on `nextAt`, no more charges. */
      ending: boolean;
      /** A Stripe trial with a card on file: the trial ends (and the first charge is) on `nextAt`. */
      trial?: true;
    }
  | { kind: "pastDue"; interval: "month" | "year"; dueCents: number; since?: number; graceEndsAt?: number }
  | { kind: "locked"; reason: "trial" | "canceled"; since?: number; keepUntil?: number };

/** The one banner at the top of the app (priority: payment failed > read-only > trial). */
export type BillingBanner =
  | { kind: "pastDue"; tone: "danger"; graceEndsAt?: number; action?: "updateCard" }
  | { kind: "locked"; tone: "danger" | "neutral"; reason: "trial" | "canceled"; action?: "subscribe" }
  | { kind: "trial"; tone: "info" | "warning" | "danger"; daysLeft: number; trialEndsAt: number; dismissible: boolean; action?: "choosePlan" | "subscribe" };

export interface BillingMeters {
  members: { used: number; limit?: number; pendingInvites: number; left?: number };
  /** Absent while the server has no byte figure (never faked). */
  storage?: { usedBytes: number; limitBytes?: number; addonBytes: number; addons: number };
}

export interface BillingView {
  card: BillingCard;
  /** Owners and admins on a client that sells: prices, Checkout, the portal. */
  manage: boolean;
  /** The banner at the top of the Billing page itself (past due, read-only). */
  pageBanner?: BillingBanner;
  /** Monthly and active: one quiet row offering annual billing, with the yearly saving in cents. */
  annualNudge?: { savingCents: number };
  /** "Need more than 25 members? Contact us" (managers on a limited plan). */
  proRow: boolean;
  meters: BillingMeters;
  /** Writes are refused: the composer gives way to a notice. */
  readOnly: boolean;
}

export type BillingViewInput = Pick<
  WorkspaceBilling,
  "plan" | "status" | "interval" | "currency" | "trialEndsAt" | "currentPeriodEnd" | "cancelAtPeriodEnd" | "pastDueSince" | "lockedAt" | "access" | "storageAddons" | "limits" | "usage" | "canManage"
>;

/** The plan's prices in a currency (undefined for a plan without prices). */
function pricesIn(plan: Plan, currency: BillingCurrency): Pick<PlanSpec, "prices" | "addonMonthlyPrice"> | undefined {
  const spec = PLAN_CATALOG[plan] as PlanSpec | undefined;
  return currency === "brl" ? (spec?.brl ?? spec) : spec;
}

/** One storage add-on per month, in cents of `currency`. */
export function addonMonthlyCents(plan: Plan, currency: BillingCurrency = "usd"): number {
  return pricesIn(plan, currency)?.addonMonthlyPrice ?? 0;
}

/** One period's price in cents for `interval`, add-ons included. */
export function periodCents(plan: Plan, interval: "month" | "year", addons: number, currency: BillingCurrency = "usd"): number {
  const base = pricesIn(plan, currency)?.prices?.[interval] ?? 0;
  const addon = addonMonthlyCents(plan, currency) * (interval === "year" ? 12 : 1);
  return base + addon * Math.max(0, addons);
}

/** The per-month figure shown for an interval ($49 billed yearly, $59 monthly). */
export function monthlyCents(plan: Plan, interval: "month" | "year", currency: BillingCurrency = "usd"): number {
  const p = pricesIn(plan, currency)?.prices;
  if (p === undefined) return 0;
  return interval === "year" ? Math.round(p.year / 12) : p.month;
}

/** What paying yearly saves over a year of monthly billing, in cents and whole percent (17). */
export function annualSaving(plan: Plan, currency: BillingCurrency = "usd"): { cents: number; percent: number } {
  const p = pricesIn(plan, currency)?.prices;
  if (p === undefined) return { cents: 0, percent: 0 };
  const cents = p.month * 12 - p.year;
  return { cents, percent: Math.round((cents / (p.month * 12)) * 100) };
}

/** Settings → Billing for this caller. `sells` is false on iOS and Android (they never show prices or buy). */
export function billingView(b: BillingViewInput, now: number, sells: boolean): BillingView {
  const spec = PLAN_CATALOG[b.plan] as PlanSpec | undefined;
  const founding = spec?.billable !== true;
  const manage = sells && b.canManage && !founding;
  const interval = b.interval ?? "month";
  const readOnly = !founding && b.access === "locked";
  let card: BillingCard;
  if (founding) card = { kind: "founding" };
  else if (readOnly) {
    const reason = b.status === "trialing" ? "trial" : "canceled";
    card = { kind: "locked", reason, ...(b.lockedAt === undefined ? {} : { since: b.lockedAt, keepUntil: b.lockedAt + LOCKED_RETENTION_DAYS * DAY_MS }) };
  } else if (b.status === "trialing" && b.currentPeriodEnd === undefined && b.trialEndsAt !== undefined) {
    const total = spec?.trialDays ?? 14;
    const left = trialDaysLeft(b, now);
    card = { kind: "trial", daysLeft: left, trialEndsAt: b.trialEndsAt, day: Math.min(total, Math.max(1, total - left + 1)), totalDays: total };
  } else if (b.status === "past_due") {
    card = {
      kind: "pastDue",
      interval,
      dueCents: periodCents(b.plan, interval, b.storageAddons, b.currency),
      ...(b.pastDueSince === undefined ? {} : { since: b.pastDueSince, graceEndsAt: b.pastDueSince + PAST_DUE_GRACE_DAYS * DAY_MS }),
    };
  } else {
    // Active, a Stripe trial with a card on file, or canceled/expired still inside the paid period.
    const ending = b.cancelAtPeriodEnd || b.status === "canceled" || b.status === "expired";
    const nextAt = b.status === "trialing" ? (b.trialEndsAt ?? b.currentPeriodEnd) : b.currentPeriodEnd;
    card = { kind: "active", interval, monthlyCents: monthlyCents(b.plan, interval, b.currency), nextCents: periodCents(b.plan, interval, b.storageAddons, b.currency), ...(nextAt === undefined ? {} : { nextAt }), ending };
    if (b.status === "trialing") card.trial = true;
  }
  const limits = founding ? {} : b.limits;
  const members: BillingMeters["members"] = { used: b.usage.members, pendingInvites: b.usage.pendingInvites };
  if (limits.members !== undefined) {
    members.limit = limits.members;
    members.left = Math.max(0, limits.members - b.usage.members);
  }
  const meters: BillingMeters = { members };
  if (b.usage.storageBytes !== undefined) {
    meters.storage = { usedBytes: b.usage.storageBytes, addonBytes: spec?.addonBytes ?? 0, addons: b.storageAddons };
    if (limits.storageBytes !== undefined) meters.storage.limitBytes = limits.storageBytes;
  }
  const view: BillingView = { card, manage, proRow: manage && limits.members !== undefined, meters, readOnly };
  if (card.kind === "pastDue" || card.kind === "locked") {
    const banner = chromeBanner(b, now, sells);
    if (banner !== null) view.pageBanner = banner;
  }
  if (manage && card.kind === "active" && card.interval === "month" && !card.ending) view.annualNudge = { savingCents: annualSaving(b.plan, b.currency).cents };
  return view;
}

/**
 * The banner across the top of the app, or null. Founding never has one. Payment failed: owners and admins only.
 * Read-only: everyone (managers get Subscribe in danger; members a neutral note). Trial: managers from 7 days left
 * (info, dismissible), 3 (warning, dismissible), and everyone on the last day (danger, not dismissible; no button for
 * members). `snoozedUntil` hides a dismissible banner until then. Native (`sells` false) drops the purchase buttons.
 */
export function chromeBanner(b: BillingViewInput, now: number, sells: boolean, snoozedUntil?: number): BillingBanner | null {
  if ((PLAN_CATALOG[b.plan] as PlanSpec | undefined)?.billable !== true) return null;
  const manager = b.canManage;
  if (b.access === "locked") {
    const reason = b.status === "trialing" ? "trial" : "canceled";
    return manager ? { kind: "locked", tone: "danger", reason, ...(sells ? { action: "subscribe" as const } : {}) } : { kind: "locked", tone: "neutral", reason };
  }
  if (b.status === "past_due") {
    if (!manager) return null;
    return {
      kind: "pastDue",
      tone: "danger",
      ...(b.pastDueSince === undefined ? {} : { graceEndsAt: b.pastDueSince + PAST_DUE_GRACE_DAYS * DAY_MS }),
      ...(sells ? { action: "updateCard" as const } : {}),
    };
  }
  // A Stripe trial with a card on file renews by itself: no banner.
  if (b.status !== "trialing" || b.trialEndsAt === undefined || b.currentPeriodEnd !== undefined) return null;
  const left = trialDaysLeft(b, now);
  if (left <= 0 || left > TRIAL_BANNER_DAYS.info) return null;
  if (left <= TRIAL_BANNER_DAYS.danger) {
    return { kind: "trial", tone: "danger", daysLeft: left, trialEndsAt: b.trialEndsAt, dismissible: false, ...(manager && sells ? { action: "subscribe" as const } : {}) };
  }
  if (!manager) return null;
  if (snoozedUntil !== undefined && now < snoozedUntil) return null;
  const tone = left <= TRIAL_BANNER_DAYS.warning ? "warning" : "info";
  return { kind: "trial", tone, daysLeft: left, trialEndsAt: b.trialEndsAt, dismissible: true, ...(sells ? { action: "choosePlan" as const } : {}) };
}

/** The invite sheet's member-limit prompt: no room at all, or fewer seats left than invitations entered. */
export type InviteRoom = { kind: "full"; members: number; limit: number } | { kind: "over"; members: number; limit: number; left: number; remove: number };

/** The prompt for `entering` new email invitations, or null when they fit (or the plan has no member limit). */
export function inviteRoom(b: Pick<WorkspaceBilling, "plan" | "storageAddons" | "usage">, entering: number): InviteRoom | null {
  const limit = limitsFor(b).members;
  if (limit === undefined) return null;
  const members = b.usage.members;
  const left = Math.max(0, limit - members);
  if (left === 0) return { kind: "full", members, limit };
  if (entering > left) return { kind: "over", members, limit, left, remove: entering - left };
  return null;
}

/** The plan picker's summary: the per-month figure, the period's charge, and what's due today. */
export interface PickerQuote {
  monthlyCents: number;
  periodCents: number;
  /** 0 when the trial carries over into the subscription (Checkout keeps more than 48 hours of it). */
  dueTodayCents: number;
  /** The first charge when the trial carries over. */
  firstChargeAt?: number;
  savePercent: number;
}

export function pickerQuote(b: Pick<WorkspaceBilling, "plan" | "status" | "trialEndsAt" | "currency">, interval: "month" | "year", addons: number, now: number): PickerQuote {
  const plan: Plan = PLAN_CATALOG[b.plan]?.billable === true ? b.plan : "starter";
  const period = periodCents(plan, interval, addons, b.currency);
  const keepsTrial = b.status === "trialing" && b.trialEndsAt !== undefined && b.trialEndsAt - now >= CHECKOUT_TRIAL_MIN_MS;
  return {
    monthlyCents: monthlyCents(plan, interval, b.currency),
    periodCents: period,
    dueTodayCents: keepsTrial ? 0 : period,
    ...(keepsTrial && b.trialEndsAt !== undefined ? { firstChargeAt: b.trialEndsAt } : {}),
    savePercent: annualSaving(plan, b.currency).percent,
  };
}

// ---------------------------------------------------------------------------------------------------------------------
// App Store and Google Play subscriptions (docs/features/billing.md → Stores). Stores can't sell quantities, so each
// Starter interval × storage add-on count (0–4) is its own product in one subscription group (Apple) / its own
// subscription with a `monthly` and a `yearly` base plan (Google Play); moving between them is an upgrade/downgrade.
// Store prices are the Stripe price × 1.15 (the stores' 15% fee), rounded up to the next real App Store price point
// ending in .99 (Google Play takes any price and uses the same numbers). Only the native apps show them.
// ---------------------------------------------------------------------------------------------------------------------

/** Who bills the workspace: Stripe (web and desktop), the App Store or Google Play. */
export type BillingSource = "stripe" | "app_store" | "play";

/** The store markup over the Stripe price. */
export const STORE_MARKUP = 1.15;

/** Store prices in US cents by interval, indexed by storage add-on count (0–4). Kept ≥ Stripe × STORE_MARKUP. */
export const STORE_PRICES: Readonly<Partial<Record<Plan, { month: readonly number[]; year: readonly number[] }>>> = {
  starter: {
    month: [6_799, 7_399, 7_999, 8_599, 9_099],
    year: [67_999, 74_999, 81_999, 88_999, 95_999],
  },
};

/** The most storage add-ons a store product carries (STORAGE_ADDON_CHOICES' top). */
export const STORE_MAX_ADDONS = 4;

/** A store product: what it sells. */
export interface StoreProduct {
  plan: Plan;
  interval: "month" | "year";
  storageAddons: number;
}

/** App Store product id: `starter_monthly`, `starter_yearly_s2` (add-on count after `_s`). */
export function appStoreProductId(p: StoreProduct): string {
  return `${p.plan}_${p.interval === "year" ? "yearly" : "monthly"}${p.storageAddons > 0 ? `_s${p.storageAddons}` : ""}`;
}

/** Google Play subscription id and base plan: `starter_s2` + `yearly`. */
export function playProductId(p: StoreProduct): { productId: string; basePlanId: "monthly" | "yearly" } {
  return { productId: `${p.plan}${p.storageAddons > 0 ? `_s${p.storageAddons}` : ""}`, basePlanId: p.interval === "year" ? "yearly" : "monthly" };
}

const isPlan = (s: string): s is Plan => s in PLAN_CATALOG && PLAN_CATALOG[s as Plan].billable;

/** The product an App Store product id sells, or undefined for an id we don't sell. */
export function parseAppStoreProductId(id: string): StoreProduct | undefined {
  const m = /^([a-z]+)_(monthly|yearly)(?:_s([1-9]))?$/.exec(id);
  if (!m || !isPlan(m[1]!)) return undefined;
  const storageAddons = m[3] ? Number(m[3]) : 0;
  if (storageAddons > STORE_MAX_ADDONS) return undefined;
  return { plan: m[1]!, interval: m[2] === "yearly" ? "year" : "month", storageAddons };
}

/** The product a Google Play subscription id + base plan sells, or undefined. */
export function parsePlayProductId(productId: string, basePlanId: string): StoreProduct | undefined {
  const m = /^([a-z]+)(?:_s([1-9]))?$/.exec(productId);
  if (!m || !isPlan(m[1]!) || (basePlanId !== "monthly" && basePlanId !== "yearly")) return undefined;
  const storageAddons = m[2] ? Number(m[2]) : 0;
  if (storageAddons > STORE_MAX_ADDONS) return undefined;
  return { plan: m[1]!, interval: basePlanId === "yearly" ? "year" : "month", storageAddons };
}

/** Every store product we sell, in catalog order (what fastlane creates and the apps load). */
export function storeProducts(): StoreProduct[] {
  const out: StoreProduct[] = [];
  for (const plan of Object.keys(STORE_PRICES) as Plan[]) {
    for (const interval of ["month", "year"] as const) {
      for (let n = 0; n <= STORE_MAX_ADDONS; n++) out.push({ plan, interval, storageAddons: n });
    }
  }
  return out;
}

/** The US-dollar store price of a product in cents (the stores localize it; apps show the store's own string). */
export function storePriceCents(p: StoreProduct): number {
  return STORE_PRICES[p.plan]?.[p.interval][p.storageAddons] ?? 0;
}

/**
 * A store subscription as the API reads it from Apple or Google, before it becomes the workspace's row:
 * - `active`: paid through `expiresAt` (`autoRenew` false ⇒ it ends then);
 * - `grace`: a renewal failed and the store's grace period keeps access (Apple grace period, Play IN_GRACE_PERIOD);
 * - `retry`: a renewal failed and the store keeps retrying without access (Apple billing retry, Play ON_HOLD);
 * - `expired`: ended; `revoked`: refunded or revoked (access ends at `revokedAt`); `pending`: not paid yet.
 */
export interface StoreSubscriptionState {
  product: StoreProduct;
  state: "active" | "grace" | "retry" | "expired" | "revoked" | "pending";
  expiresAt: number;
  autoRenew: boolean;
  revokedAt?: number;
}

/**
 * Our status for a store subscription, or undefined while it says nothing yet (`pending`). A failed renewal is
 * `past_due` from the old expiry (the same 14 days of full access as a failed card payment); a refund ends access at once.
 */
export function statusFromStore(s: StoreSubscriptionState, now: number): { status: BillingStatus; currentPeriodEnd: number; cancelAtPeriodEnd: boolean; pastDueSince?: number } | undefined {
  switch (s.state) {
    case "pending":
      return undefined;
    case "active":
      return s.expiresAt > now
        ? { status: "active", currentPeriodEnd: s.expiresAt, cancelAtPeriodEnd: !s.autoRenew }
        : { status: s.autoRenew ? "past_due" : "expired", currentPeriodEnd: s.expiresAt, cancelAtPeriodEnd: !s.autoRenew, ...(s.autoRenew ? { pastDueSince: s.expiresAt } : {}) };
    case "grace":
    case "retry":
      return { status: "past_due", currentPeriodEnd: s.expiresAt, cancelAtPeriodEnd: false, pastDueSince: s.expiresAt };
    case "expired":
      return { status: "expired", currentPeriodEnd: s.expiresAt, cancelAtPeriodEnd: true };
    case "revoked":
      return { status: "canceled", currentPeriodEnd: Math.min(s.revokedAt ?? now, s.expiresAt), cancelAtPeriodEnd: true };
  }
}

/** Whether a row billed by `source` in `status` holds the workspace (another source can't start a second subscription). */
export function sourceHoldsWorkspace(source: BillingSource | undefined, status: BillingStatus, currentPeriodEnd: number | undefined, now: number): boolean {
  if (source === undefined) return false;
  if (status === "active" || status === "past_due") return true;
  // Canceled or expired but still inside the paid period: the other source waits until it runs out.
  if (status === "canceled" || status === "expired") return (currentPeriodEnd ?? 0) > now;
  // A Stripe trial with a card (trialing + a subscription) holds it too.
  return source === "stripe" && status === "trialing" && currentPeriodEnd !== undefined;
}

/** The store billing the workspace right now, if one is: web and desktop then sell nothing (no Checkout, portal or
 * purchase buttons) and show who manages it where. */
export function storeHolding(b: Pick<WorkspaceBilling, "source" | "status" | "currentPeriodEnd">, now: number): Exclude<BillingSource, "stripe"> | undefined {
  return b.source !== undefined && b.source !== "stripe" && sourceHoldsWorkspace(b.source, b.status, b.currentPeriodEnd, now) ? b.source : undefined;
}

/**
 * What the native apps' store section shows on Settings → Billing (and the plan card's "billed through" line), for
 * the app's own store. Founding workspaces and members get `none` (members keep the read-only summary):
 * - `subscribe`: no source holds the workspace: the plan picker with "Subscribe with Apple/Google" and Restore purchases;
 * - `manage`: this store bills it and the caller bought it: "Manage in App Store / Google Play" (the system page);
 * - `managedByOther`: this store bills it through someone else's account: who to ask;
 * - `otherStore`: the other store bills it (managed on that platform);
 * - `card`: Stripe bills it: "Billed by card", managed on the web or desktop (no purchase offered).
 * Web and desktop use `source` the same way: a store-billed workspace shows `otherStore` instead of Checkout and the portal.
 */
export type StoreOffer =
  | { kind: "none" }
  | { kind: "subscribe" }
  | { kind: "manage"; store: Exclude<BillingSource, "stripe"> }
  | { kind: "managedByOther"; store: Exclude<BillingSource, "stripe">; by?: string }
  | { kind: "otherStore"; store: Exclude<BillingSource, "stripe">; by?: string }
  | { kind: "card" };

export function storeOffer(
  b: Pick<WorkspaceBilling, "plan" | "status" | "currentPeriodEnd" | "canManage" | "source" | "storeManagedBy" | "storeManagedByMe">,
  store: Exclude<BillingSource, "stripe">,
  now: number,
): StoreOffer {
  if (PLAN_CATALOG[b.plan]?.billable !== true || !b.canManage) return { kind: "none" };
  const holds = sourceHoldsWorkspace(b.source, b.status, b.currentPeriodEnd, now);
  if (!holds || b.source === undefined) return { kind: "subscribe" };
  const by = b.storeManagedBy === undefined ? {} : { by: b.storeManagedBy };
  if (b.source === "stripe") return { kind: "card" };
  if (b.source !== store) return { kind: "otherStore", store: b.source, ...by };
  return b.storeManagedByMe === true ? { kind: "manage", store } : { kind: "managedByOther", store, ...by };
}
